import classnames from 'classnames';
import {
	useBlockProps,
	InnerBlocks,
	__experimentalGetBorderClassesAndStyles as getBorderClassesAndStyles,
	__experimentalGetColorClassesAndStyles as getColorClassesAndStyles,
	__experimentalGetSpacingClassesAndStyles as getSpacingClassesAndStyles,
} from '@wordpress/block-editor';
import { createBlock } from '@wordpress/blocks';

const blockAttributes = {
	id: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'id',
	},
	uuid: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-id',
	},
	align: {
		type: 'string',
		default: 'center center',
	},
	anchor: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-anchor',
		default: '',
	},
	animation: {
		type: 'string',
	},
	boxShadow: {
		type: 'string',
		default: 'wp-block-popper__shadow-md',
	},
	borderRadius: {
		type: 'string',
		default: 'wp-block-popper__border-md',
	},
	closeAnchor: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-anchorclose',
	},
	closeOnAnchorClick: {
		type: 'boolean',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-anchorclose',
	},
	closeOnClickOutside: {
		type: 'boolean',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-outside',
		default: true,
	},
	closeOnFormSubmission: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-form',
	},
	devices: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-devices',
		default: 'desktop,tablet,mobile',
	},
	dismissForVisitors: {
		type: 'boolean',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-dismiss',
	},
	dismissPeriod: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-dismiss',
		default: '3',
	},
	fullPage: {
		type: 'boolean',
		selector: 'div.wp-block-popper.wide',
		default: false,
	},
	fullWidth: {
		type: 'boolean',
		selector: 'div.wp-block-popper.alignfull',
		default: false,
	},
	offset: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-offset',
		default: 20,
	},
	openBehaviour: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-open',
	},
	backgroundColor: {
		type: 'string',
	},
	overlayColor: {
		type: 'string',
	},
	pageviews: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-pagenum',
	},
	target: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-target',
		default: '',
	},
	waitTime: {
		type: 'string',
		source: 'attribute',
		selector: 'div.wp-block-popper',
		attribute: 'data-time',
		default: 1,
	},
	minWidth: {
		type: 'string',
	},
};

const blockAttributesV2 = {
	id: {
		type: 'string',
		source: 'attribute',
		selector: 'dialog.wp-block-popper',
		attribute: 'id',
	},
	uuid: {
		type: 'number',
	},
	devices: {
		type: 'array',
	},
	triggers: {
		type: 'array',
		default: [],
	},
	settings: {
		type: 'object',
		default: {},
	},
	align: {
		type: 'string',
		source: 'attribute',
		selector: 'dialog.wp-block-popper',
		attribute: 'data-align',
		default: 'center center',
	},
	animation: {
		type: 'string',
	},
	boxShadow: {
		type: 'string',
		default: 'wp-block-popper__shadow-md',
	},
	borderRadius: {
		type: 'string',
		default: 'wp-block-popper__border-md',
	},
	fullPage: {
		type: 'boolean',
		selector: 'dialog.wp-block-popper.wide',
		default: false,
	},
	fullWidth: {
		type: 'boolean',
		selector: 'dialog.wp-block-popper.alignfull',
		default: false,
	},
	disableScroll: {
		type: 'boolean',
		selector: 'dialog.wp-block-popper.no-scroll',
		default: false,
	},
	overlayColor: {
		type: 'string',
	},
	minWidth: {
		type: 'string',
	},
};

const supports = {
	html: false,
	multiple: false,
	lock: false,
	className: false,
	reusable: false,
	inserter: false,
	spacing: {
		__experimentalSkipSerialization: true,
		padding: true,
		margin: true,
	},
	__experimentalBorder: {
		__experimentalSkipSerialization: true,
		color: true,
		radius: true,
		style: true,
		width: true,
		__experimentalDefaultControls: {
			color: true,
			radius: true,
			style: true,
			width: true,
		},
	},
};

const v1 = {
	attributes: blockAttributes,
	supports,
	migrate( {
		id,
		devices,
		openBehaviour,
		anchor,
		anchorClose,
		dismissForVisitors,
		offset,
		waitTime,
		target,
		pageviews,
		...restAttributes
	} ) {
		const settings = {};
		if ( anchor ) {
			settings.anchor = {
				anchor,
			};
		}
		if ( offset ) {
			settings.adblock = {
				offset,
			};
		}
		if ( offset ) {
			settings.scroll = {
				offset,
			};
		}
		if ( waitTime ) {
			settings.timer = {
				time: waitTime,
			};
		}
		if ( target ) {
			settings.target = {
				anchor: target,
			};
		}
		if ( pageviews ) {
			settings.pageviews = {
				pageNum: pageviews,
			};
		}
		if ( anchorClose ) {
			settings.closeAnchor = {
				target: anchorClose,
			};
		}
		if ( dismissForVisitors ) {
			settings.dismissForVisitors = {
				period: dismissForVisitors,
			};
		}
		if ( 'load' === openBehaviour ) {
			openBehaviour = 'timer';
			settings.timer = {
				time: '0',
			};
		}
		return {
			id: id.replace( 'modal-', 'popper-' ),
			triggers: openBehaviour.split( ',' ),
			devices: devices.split( ',' ),
			settings,
			...restAttributes,
		};
	},
	save( { attributes, className } ) {
		const {
			minWidth,
			anchor,
			target,
			waitTime,
			offset,
			openBehaviour,
			backgroundColor,
			borderRadius,
			boxShadow,
			overlayColor,
			closeAnchor,
			closeOnClickOutside,
			closeOnAnchorClick,
			closeOnFormSubmission,
			dismissForVisitors,
			dismissPeriod,
			pageviews,
			animation,
			align,
			fullPage,
			fullWidth,
			id,
			uuid,
			devices,
		} = attributes;

		const borderProps = getBorderClassesAndStyles( attributes );
		const spacingProps = getSpacingClassesAndStyles( attributes );
		const colorProps = getColorClassesAndStyles( attributes );

		const modalStyle = {
			backgroundColor,
			...borderProps.style,
		};

		const contentStyle = {
			...spacingProps.style,
			...colorProps.style,
			minWidth: fullWidth ? undefined : minWidth,
		};

		const popperClass = classnames( 'wp-block-popper', className, {
			'wp-block-popper--right': align.includes( 'right' ),
			'wp-block-popper--left': align.includes( 'left' ),
			'wp-block-popper--top': align.includes( 'top' ),
			'wp-block-popper--bottom': align.includes( 'bottom' ),
		} );

		const containerClass = classnames(
			'wp-block-popper__container',
			boxShadow,
			borderRadius,
			colorProps.className,
			{
				'wp-block-popper__animate': !! animation,
				wide: fullPage,
				fullwidth: fullWidth,
			}
		);

		if ( animation ) {
			modalStyle[ '--popper-animation' ] = animation;
		}

		return (
			<div
				data-open={ openBehaviour }
				data-anchor={ openBehaviour === 'anchor' ? anchor : undefined }
				data-target={ openBehaviour === 'target' ? target : undefined }
				data-anchorclose={
					closeOnAnchorClick ? closeAnchor : undefined
				}
				data-time={ openBehaviour === 'load' ? waitTime : undefined }
				data-dismiss={ dismissForVisitors ? dismissPeriod : undefined }
				data-offset={ openBehaviour === 'scroll' ? offset : undefined }
				data-pagenum={
					openBehaviour === 'pageviews' ? pageviews : undefined
				}
				data-outside={ closeOnClickOutside }
				data-form={ closeOnFormSubmission }
				data-id={ uuid || undefined }
				data-devices={ devices }
				id={ id }
				aria-hidden="true"
				className={ popperClass }
			>
				{ 'center center' === align && ! fullPage && (
					<div
						className="wp-block-popper__overlay"
						tabIndex="-1"
						style={ { backgroundColor: overlayColor } }
					></div>
				) }
				<div
					{ ...useBlockProps.save( {
						className: containerClass,
						style: modalStyle,
					} ) }
					role="dialog"
					aria-modal="true"
					aria-labelledby="modal-title"
				>
					<main
						className="wp-block-popper__content"
						style={ contentStyle }
					>
						<InnerBlocks.Content />
					</main>
				</div>
			</div>
		);
	},
};

const v2 = {
	attributes: blockAttributesV2,
	supports,
	migrate( attributes, innerBlocks ) {
		const {
			fullPage,
			fullWidth,
			className,
			boxShadow,
			borderRadius,
			animation,
			minWidth,
			...restAttributes
		} = attributes;
		const settings = {};
		if ( fullWidth ) {
			settings.fullWidth = undefined;
		}
		if ( fullPage ) {
			settings.fullPage = undefined;
		}
		if ( minWidth ) {
			settings.minWidth = undefined;
		}
		const newAttrs = {
			settings,
			...restAttributes,
		};
		return [
			newAttrs,
			[
				createBlock( 'popper/button' ),
				createBlock( 'popper/body', { width: minWidth }, innerBlocks ),
			],
		];
	},
	save( { attributes, className } ) {
		const {
			minWidth,
			borderRadius,
			boxShadow,
			animation,
			align,
			fullPage,
			fullWidth,
			id,
			uuid,
			devices,
			triggers,
			settings,
			disableScroll,
		} = attributes;

		const modalStyle = {
			width: fullWidth ? undefined : minWidth,
		};

		const popperClass = classnames(
			'wp-block-popper',
			boxShadow,
			borderRadius,
			className,
			{
				'wp-block-popper__animate': !! animation,
				'no-scroll': disableScroll,
				wide: fullPage,
				fullwidth: fullWidth,
			}
		);

		if ( animation ) {
			modalStyle[ '--popper-animation' ] = animation;
		}

		return (
			<dialog
				{ ...useBlockProps.save( {
					className: popperClass,
					style: modalStyle,
				} ) }
				data-id={ uuid }
				data-modal={ 'center center' === align || undefined }
				data-triggers={ JSON.stringify( triggers ) }
				data-devices={ JSON.stringify( devices ) }
				data-settings={ JSON.stringify( settings ) }
				data-align={ align }
				id={ id }
			>
				<InnerBlocks.Content />
			</dialog>
		);
	},
};

const v3 = {
	attributes: blockAttributesV2,
	supports,
	isEligible: () => {
		return true;
	},
	migrate( attributes ) {
		const {
			fullPage,
			fullWidth,
			className,
			boxShadow,
			borderRadius,
			animation,
			minWidth,
			...restAttributes
		} = attributes;
		const settings = {};
		if ( fullWidth ) {
			settings.fullWidth = undefined;
		}
		if ( fullPage ) {
			settings.fullPage = undefined;
		}
		if ( minWidth ) {
			settings.minWidth = undefined;
		}
		const newAttrs = {
			settings,
			...restAttributes,
		};
		return [ newAttrs ];
	},
	save( { attributes, className } ) {
		const {
			minWidth,
			borderRadius,
			boxShadow,
			animation,
			align,
			id,
			uuid,
			disableScroll,
		} = attributes;

		const modalStyle = {
			width:
				-1 !== className?.indexOf( 'is-style-top-bar' ) ||
				-1 !== className?.indexOf( 'is-style-bottom-bar' )
					? minWidth
					: undefined,
		};

		const popperClass = classnames(
			'wp-block-popper',
			boxShadow,
			borderRadius,
			className,
			{
				'wp-block-popper__animate': !! animation,
				'no-scroll': disableScroll,
			}
		);

		const borderProps = getBorderClassesAndStyles( attributes );
		const spacingProps = getSpacingClassesAndStyles( attributes );
		const colorProps = getColorClassesAndStyles( attributes );

		if (
			animation &&
			! className?.includes( 'is-style-off-canvas' ) &&
			! className?.includes( 'is-style-off-canvas-left' )
		) {
			modalStyle[ '--popper-animation' ] = animation;
		}

		return (
			<dialog
				{ ...useBlockProps.save( {
					className: popperClass,
					style: {
						...borderProps.style,
						...spacingProps.style,
						...colorProps.style,
					},
				} ) }
				data-id={ uuid }
				data-modal={ 'center center' === align || undefined }
				data-align={ align }
				id={ id }
			>
				<InnerBlocks.Content />
			</dialog>
		);
	},
};

const deprecated = [ v2, v1 ];

export default deprecated;
